import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper, Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Autocomplete, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Switch, Alert,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import {
  useUserMemberships, useCreateOrgUnitMember, useUpdateOrgUnitMember, useDeleteOrgUnitMember,
} from '@/hooks/core/useOrgUnitMember.custom';
import { useOrgUnits } from '@/hooks/core/useOrgUnit';
import { useOrgRoleFunctions } from '@/hooks/core/useOrgRoleFunction';
import { MembershipStateChip } from './MembershipStateChip';
import { formatValidityBound, isoToDateInput, validityBoundToIso } from './orgUnitMembership';
import { OrgUnit } from '@/types/core/OrgUnit';
import { OrgUnitMember } from '@/types/core/OrgUnitMember';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';

/**
 * User-centric org-membership management: from a user, see and edit which org units they
 * belong to and in what function. Complements the org-centric Members tab on the
 * Organization tree (same OrgUnitMember data, the other entry point).
 */
export function UserOrgMembershipsCard({ userId, userName }: { userId: string; userName: string }) {
  const { t } = useTranslation();
  const { data: memberships, isLoading } = useUserMemberships(userId);
  const { data: orgUnits } = useOrgUnits();
  const deleteMutation = useDeleteOrgUnitMember();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgUnitMember | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const orgUnitName = (ouId: string) => (orgUnits as OrgUnit[] | undefined)?.find(o => o.id === ouId)?.name ?? ouId;

  const handleDelete = (m: OrgUnitMember) => {
    if (!window.confirm(t('orgUnit.removeMemberConfirm', { name: orgUnitName(m.orgUnitId) }))) return;
    setActionError(null);
    deleteMutation.mutate({ id: m.id, orgUnitId: m.orgUnitId }, {
      onError: () => setActionError(t('orgUnit.removeMemberError', { defaultValue: 'Could not remove membership' })),
    });
  };

  const columns: GridColDef<OrgUnitMember>[] = useMemo(() => [
    {
      field: 'actions', type: 'actions', headerName: '', width: 80,
      getActions: (p) => [
        <GridActionsCellItem key="edit" icon={<Edit fontSize="small" />} label={t('common.edit')} onClick={() => { setEditing(p.row); setDialogOpen(true); }} />,
        <GridActionsCellItem key="delete" icon={<Delete fontSize="small" />} label={t('common.delete')} onClick={() => handleDelete(p.row)} />,
      ],
    },
    { field: 'orgUnitId', headerName: t('orgUnit.title', { defaultValue: 'Org Unit' }), width: 240, renderCell: (p) => orgUnitName(p.value) },
    { field: 'role', headerName: t('common.role'), width: 160, renderCell: (p) => p.value || '—' },
    { field: 'isPrimary', headerName: t('orgUnit.primary'), width: 90, renderCell: (p) => p.value ? <Chip label={t('common.yes')} size="small" color="primary" variant="outlined" /> : null },
    // Effective state, not the raw isActive flag — see orgUnitMembership.ts.
    { field: 'isActive', headerName: t('orgUnit.membershipStatus', { defaultValue: 'Status' }), width: 120, renderCell: (p) => <MembershipStateChip member={p.row} /> },
    { field: 'validFrom', headerName: t('orgUnit.validFrom', { defaultValue: 'Valid from' }), width: 110, renderCell: (p) => formatValidityBound(p.value) },
    { field: 'validTo', headerName: t('orgUnit.validTo', { defaultValue: 'Valid to' }), width: 110, renderCell: (p) => formatValidityBound(p.value) },
    { field: '_spacer', headerName: '', flex: 1, sortable: false, filterable: false, disableColumnMenu: true, resizable: false },
  ], [t, orgUnits, deleteMutation.isPending]);

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => { setEditing(null); setDialogOpen(true); }}>
          {t('user.addMembership', { defaultValue: 'Add to org unit' })}
        </Button>
        <Typography variant="subtitle2">{t('user.orgMemberships', { defaultValue: 'Org Memberships' })}</Typography>
      </Box>
      {actionError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      <DataGridPremium
        rows={memberships || []}
        columns={columns}
        loading={isLoading}
        density="compact"
        rowHeight={GRID_ROW_HEIGHT}
        autoHeight
        disableRowSelectionOnClick
        hideFooter={(memberships?.length ?? 0) <= 10}
      />
      <MembershipDialog
        open={dialogOpen}
        userId={userId}
        userName={userName}
        member={editing}
        orgUnits={(orgUnits as OrgUnit[]) || []}
        orgUnitName={orgUnitName}
        onClose={() => setDialogOpen(false)}
        onSaved={() => setDialogOpen(false)}
      />
    </Paper>
  );
}

interface DialogProps {
  open: boolean;
  userId: string;
  userName: string;
  member: OrgUnitMember | null;
  orgUnits: OrgUnit[];
  orgUnitName: (id: string) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MembershipDialog({ open, userId, userName, member, orgUnits, orgUnitName, onClose, onSaved }: DialogProps) {
  const { t } = useTranslation();
  const isEdit = !!member;
  const createMutation = useCreateOrgUnitMember();
  const updateMutation = useUpdateOrgUnitMember();
  const { data: roleFunctions } = useOrgRoleFunctions();

  const [orgUnitId, setOrgUnitId] = useState(member?.orgUnitId ?? '');
  const [role, setRole] = useState(member?.role ?? '');
  const [isPrimary, setIsPrimary] = useState(member?.isPrimary ?? false);
  const [isActive, setIsActive] = useState(member?.isActive ?? true);
  const [validFrom, setValidFrom] = useState(isoToDateInput(member?.validFrom));
  const [validTo, setValidTo] = useState(isoToDateInput(member?.validTo));
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Re-seed local state when the dialog opens for a different member.
  const key = `${open}:${member?.id ?? 'new'}`;
  const [seededKey, setSeededKey] = useState('');
  if (open && seededKey !== key) {
    setSeededKey(key);
    setOrgUnitId(member?.orgUnitId ?? '');
    setRole(member?.role ?? '');
    setIsPrimary(member?.isPrimary ?? false);
    setIsActive(member?.isActive ?? true);
    setValidFrom(isoToDateInput(member?.validFrom));
    setValidTo(isoToDateInput(member?.validTo));
    setDialogError(null);
  }

  const activeRoles = (roleFunctions ?? []).filter(r => r.isActive);
  const pending = createMutation.isPending || updateMutation.isPending;
  const rangeInvalid = !!validFrom && !!validTo && validTo < validFrom;

  const handleSubmit = () => {
    setDialogError(null);
    if (isEdit && member) {
      updateMutation.mutate({ id: member.id, data: {
        orgUnitId: member.orgUnitId, userId: member.userId, userName: member.userName,
        role: role || undefined, isPrimary, isActive, source: member.source,
        validFrom: validityBoundToIso(validFrom, 'from'), validTo: validityBoundToIso(validTo, 'to'),
      } }, { onSuccess: () => onSaved(), onError: (e) => setDialogError(e.message) });
      return;
    }
    if (!orgUnitId) return;
    createMutation.mutate({
      orgUnitId, userId, userName, role: role || undefined, isPrimary, isActive, source: 'MANUAL',
      validFrom: validityBoundToIso(validFrom, 'from'), validTo: validityBoundToIso(validTo, 'to'),
    }, { onSuccess: () => onSaved(), onError: (e) => setDialogError(e.message) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? t('user.editMembership', { defaultValue: 'Edit membership' }) : t('user.addMembership', { defaultValue: 'Add to org unit' })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {dialogError && <Alert severity="error" onClose={() => setDialogError(null)}>{dialogError}</Alert>}
        <TextField label={t('common.user')} value={userName} size="small" disabled fullWidth />
        {isEdit ? (
          <TextField label={t('orgUnit.title', { defaultValue: 'Org Unit' })} value={orgUnitName(orgUnitId)} size="small" disabled fullWidth />
        ) : (
          <Autocomplete
            options={orgUnits}
            getOptionLabel={(o: OrgUnit) => o.code ? `${o.name} (${o.code})` : o.name}
            value={orgUnits.find(o => o.id === orgUnitId) ?? null}
            onChange={(_e, v) => setOrgUnitId(v?.id ?? '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label={t('orgUnit.title', { defaultValue: 'Org Unit' })} size="small" />}
          />
        )}
        <FormControl size="small" fullWidth>
          <InputLabel>{t('common.role')}</InputLabel>
          <Select value={role} label={t('common.role')} displayEmpty onChange={(e) => setRole(e.target.value as string)}>
            <MenuItem value=""><em>{t('common.none', { defaultValue: 'None' })}</em></MenuItem>
            {activeRoles.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={t('orgUnit.validFrom', { defaultValue: 'Valid from' })}
            type="date" size="small" fullWidth value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('orgUnit.validityOptionalHint', { defaultValue: 'Optional — empty means no limit' })}
          />
          <TextField
            label={t('orgUnit.validTo', { defaultValue: 'Valid to' })}
            type="date" size="small" fullWidth value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={rangeInvalid}
            helperText={rangeInvalid
              ? t('orgUnit.validityRangeError', { defaultValue: 'Valid to must not be before Valid from' })
              : t('orgUnit.validityInclusiveHint', { defaultValue: 'Inclusive — valid through this day' })}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <FormControlLabel control={<Checkbox checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />} label={t('orgUnit.primary')} />
          {isEdit && <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label={t('common.active')} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={(!isEdit && !orgUnitId) || pending || rangeInvalid}>
          {pending ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
